// src/components/booking/DateProposalModal.tsx
//
// Modal "Proponi tu le date": form parallelo al BookingForm che consente
// al cliente di suggerire date personalizzate per un tour, senza vincolo
// su slot esistenti o disponibilità. La richiesta arriva al BE come
// booking con flag isProposal=true e status PENDING, gestita dall'admin
// nel pannello Proposte.

import React, { useState } from 'react';
import TurnstileWidget from '../TurnstileWidget';
import { publicApi } from '../../api/insolita';
import { Tour } from '../../api/types';

interface Props {
  tours: Tour[];
  defaultTourId?: number;
  onClose: () => void;
}

const emptyForm = {
  tourId: 0,
  checkInDate: '',
  checkOutDate: '',
  people: 2,
  customerName: '',
  customerEmail: '',
  customerPhone: '',
  notes: '',
  consent: false,
  website: '', // honeypot
};

const DateProposalModal: React.FC<Props> = ({ tours, defaultTourId, onClose }) => {
  const [form, setForm] = useState({ ...emptyForm, tourId: defaultTourId || (tours[0]?.id || 0) });
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sent, setSent] = useState(false);
  const [captchaToken, setCaptchaToken] = useState<string | null>(null);

  const submit = async (e?: React.FormEvent | React.MouseEvent) => {
    if (e) e.preventDefault();
    if (sending) return;
    if (!form.tourId) { setError('Seleziona un tour.'); return; }
    if (!form.checkInDate || !form.checkOutDate) { setError('Inserisci un periodo valido.'); return; }
    if (new Date(form.checkOutDate) < new Date(form.checkInDate)) { setError('La data di fine non può precedere quella di inizio.'); return; }
    if (!form.customerName.trim()) { setError('Inserisci nome e cognome.'); return; }
    if (!form.customerEmail.trim() || !/.+@.+\..+/.test(form.customerEmail)) { setError('Inserisci un indirizzo email valido.'); return; }
    if (!form.people || form.people < 1) { setError('Indica almeno 1 partecipante.'); return; }
    if (!form.consent) { setError('Devi acconsentire al trattamento dei dati.'); return; }
    if (!captchaToken) { setError('Completa la verifica anti-bot.'); return; }
    setSending(true);
    setError(null);
    try {
      await publicApi.createBooking({
        tourId: form.tourId,
        checkInDate: form.checkInDate,
        checkOutDate: form.checkOutDate,
        customerName: form.customerName,
        customerEmail: form.customerEmail,
        customerPhone: form.customerPhone || undefined,
        people: Math.max(1, form.people || 1),
        notes: form.notes || undefined,
        consent: form.consent,
        proposal: true,
        captchaToken,
        website: form.website,
      });
      setSent(true);
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Impossibile inviare la proposta. Riprova più tardi.');
    } finally {
      setSending(false);
    }
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
      style={{ position: 'fixed', inset: 0, zIndex: 100, background: 'rgba(0,0,0,.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}
    >
      <div style={{ background: '#141417', color: '#E8E3D6', maxWidth: 560, width: '100%', maxHeight: '92vh', overflowY: 'auto', border: '1px solid rgba(244,241,233,.14)', borderRadius: 8, padding: '28px 28px 24px' }}>
        <button aria-label="Chiudi" onClick={onClose} style={{ float: 'right', background: 'transparent', color: '#E8E3D6', border: 0, fontSize: 22, cursor: 'pointer', lineHeight: 1 }}>×</button>
        <h2 style={{ margin: '0 0 6px', color: '#F4F1E9' }}>Proponi le tue date</h2>
        <p style={{ margin: '0 0 20px', fontSize: 14, color: '#B8B8B0' }}>
          Nessuna data in calendario ti va bene? Proponi tu il periodo che preferisci: valuteremo la disponibilità e ti risponderemo entro pochi giorni.
        </p>

        {sent ? (
          <div>
            <p style={{ color: '#E4FF1A', fontWeight: 600 }}>✓ Proposta inviata</p>
            <p style={{ fontSize: 14 }}>Ti confermeremo la disponibilità via email a <b>{form.customerEmail}</b>.</p>
            <button onClick={onClose} className="btn" style={{ marginTop: 16, background: '#E4FF1A', color: '#0B0B0C', padding: '10px 20px', borderRadius: 3, border: 0, fontWeight: 700, cursor: 'pointer' }}>Chiudi</button>
          </div>
        ) : (
          // Non usiamo <form> deliberatamente: eviterebbe il submit implicito
          // HTML5 su Enter/checkbox. Il submit avviene SOLO col click sul pulsante.
          // In più intercettiamo Enter fuori dalle textarea per bloccarlo del tutto.
          <div onKeyDown={(e) => {
              if (e.key === 'Enter' && (e.target as HTMLElement).tagName !== 'TEXTAREA') {
                e.preventDefault();
              }
            }}>
            <div style={{ display: 'grid', gap: 12 }}>
              <label style={{ fontSize: 13 }}>Tour
                <select value={form.tourId} onChange={(e) => setForm({ ...form, tourId: Number(e.target.value) })}
                        style={{ width: '100%', marginTop: 6, padding: '10px 12px', background: '#0B0B0C', color: '#F4F1E9', border: '1px solid rgba(244,241,233,.14)', borderRadius: 3 }}>
                  <option value={0}>— Seleziona tour —</option>
                  {tours.map((t) => <option key={t.id} value={t.id}>{t.title}</option>)}
                </select>
              </label>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                <label style={{ fontSize: 13 }}>Data inizio
                  <input type="date" value={form.checkInDate} onChange={(e) => setForm({ ...form, checkInDate: e.target.value })} required
                         style={{ width: '100%', marginTop: 6, padding: '10px 12px', background: '#0B0B0C', color: '#F4F1E9', border: '1px solid rgba(244,241,233,.14)', borderRadius: 3 }} />
                </label>
                <label style={{ fontSize: 13 }}>Data fine
                  <input type="date" value={form.checkOutDate} onChange={(e) => setForm({ ...form, checkOutDate: e.target.value })} required
                         style={{ width: '100%', marginTop: 6, padding: '10px 12px', background: '#0B0B0C', color: '#F4F1E9', border: '1px solid rgba(244,241,233,.14)', borderRadius: 3 }} />
                </label>
              </div>
              <label style={{ fontSize: 13 }}>Persone
                <input type="number" min={1} max={20} value={form.people} onChange={(e) => setForm({ ...form, people: Number(e.target.value) })} required
                       style={{ width: '100%', marginTop: 6, padding: '10px 12px', background: '#0B0B0C', color: '#F4F1E9', border: '1px solid rgba(244,241,233,.14)', borderRadius: 3 }} />
              </label>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                <label style={{ fontSize: 13 }}>Nome e cognome
                  <input value={form.customerName} onChange={(e) => setForm({ ...form, customerName: e.target.value })} required
                         style={{ width: '100%', marginTop: 6, padding: '10px 12px', background: '#0B0B0C', color: '#F4F1E9', border: '1px solid rgba(244,241,233,.14)', borderRadius: 3 }} />
                </label>
                <label style={{ fontSize: 13 }}>Telefono
                  <input type="tel" value={form.customerPhone} onChange={(e) => setForm({ ...form, customerPhone: e.target.value })}
                         style={{ width: '100%', marginTop: 6, padding: '10px 12px', background: '#0B0B0C', color: '#F4F1E9', border: '1px solid rgba(244,241,233,.14)', borderRadius: 3 }} />
                </label>
              </div>
              <label style={{ fontSize: 13 }}>Email
                <input type="email" value={form.customerEmail} onChange={(e) => setForm({ ...form, customerEmail: e.target.value })} required
                       style={{ width: '100%', marginTop: 6, padding: '10px 12px', background: '#0B0B0C', color: '#F4F1E9', border: '1px solid rgba(244,241,233,.14)', borderRadius: 3 }} />
              </label>
              <label style={{ fontSize: 13 }}>Note (opzionale)
                <textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={3} placeholder="Es. preferiamo pomeriggio, 2 esperti + 1 principiante…"
                          style={{ width: '100%', marginTop: 6, padding: '10px 12px', background: '#0B0B0C', color: '#F4F1E9', border: '1px solid rgba(244,241,233,.14)', borderRadius: 3, resize: 'vertical' }} />
              </label>
              <label style={{ fontSize: 13, display: 'flex', alignItems: 'flex-start', gap: 8 }}>
                <input type="checkbox" checked={form.consent} onChange={(e) => setForm({ ...form, consent: e.target.checked })} required style={{ marginTop: 3 }} />
                <span>Acconsento al trattamento dei dati per la valutazione della proposta.</span>
              </label>
              {/* Honeypot antispam */}
              <div aria-hidden="true" style={{ position: 'absolute', left: '-10000px', width: 1, height: 1, overflow: 'hidden' }}>
                <label>Non compilare
                  <input type="text" tabIndex={-1} autoComplete="off" name="website" value={form.website} onChange={(e) => setForm({ ...form, website: e.target.value })} />
                </label>
              </div>
              <TurnstileWidget onToken={setCaptchaToken} theme="dark" />
              {error && <p style={{ color: '#ff6b6b', fontSize: 13, margin: 0 }}>{error}</p>}
              <button type="button" onClick={submit} disabled={sending || !captchaToken}
                      style={{ marginTop: 6, background: '#E4FF1A', color: '#0B0B0C', padding: '12px 24px', borderRadius: 3, border: 0, fontWeight: 700, fontSize: 14, textTransform: 'uppercase', letterSpacing: '.04em', cursor: (sending || !captchaToken) ? 'not-allowed' : 'pointer', opacity: (sending || !captchaToken) ? 0.6 : 1 }}>
                {sending ? 'Invio in corso…' : 'Invia proposta'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default DateProposalModal;
